//* 绘图库
import Konva from "konva";
//* 组件库
import type { UiTooltipFunction } from "various-ui";
//* 工具库
import { Formats } from "@/assets/js";
//* 组件属性
import type { ModuleData, ModuleExpression } from "../../../index";
import type { ModuleProps } from "../index";
import { type VisualData, disposeVisualData } from "./drawing";
import { useOptions } from "./options";

export const useComposable = (define: ModuleProps) => {
    //* 插件
    const route = useRoute();

    //* 配置项
    const options = useOptions();

    //* 响应式变量
    const refs = {
        mode: ref<string>(""), //* 排序模式
        tags: ref<{ name: string; color: string }[]>([]), //* 标签列表
        info: ref<ModuleExpression>(), //* 悬浮信息数据
        loading: ref<boolean>(true), //* 内置加载器
        exist: ref<boolean>(true), //* 页面状态配置
    };

    //* 节点
    const nodes = {
        body: ref<HTMLDivElement>(),
        tooltip: ref<InstanceType<typeof UiTooltipFunction>>(),
        container: ref<HTMLDivElement>(),
        animation: ref<Konva.Tween>(),
    };

    //* 静态属性
    const variable = {
        data: shallowRef<{ [name: string]: ModuleExpression[] }>({}),
        stage: shallowRef<Konva.Stage>(),
        offset: 0,
        observer: shallowRef<ResizeObserver>(),
    };

    //* 函数列表
    const methods = {
        //* 初始化函数
        render: (data?: ModuleData) => {
            //* 数据初始化
            refs.loading.value = false;
            refs.mode.value = "group";
            refs.tags.value = data?.tags || [];
            if (data?.data?.length) {
                variable.data.value[refs.mode.value] = data.data;
            }

            //* 观察者注册
            if (nodes.container.value && !variable.observer.value) {
                variable.observer.value = useResizeWObserver(nodes.container.value, (size) => {
                    if (size == 1520) variable.offset = 64;
                    else if (size == 1328) variable.offset = 56;
                    else {
                        variable.offset = 48;
                    }

                    //* 将画布尺寸设置为0，防止影响宽度分配
                    variable.stage.value?.width(0);
                    //* 渲染
                    render.main();
                });
            }
        },
        //* 隐藏函数
        hidden: () => {
            refs.exist.value = false;
            refs.loading.value = false;
        },
        //* 排序模式切换函数
        mode: () => {
            if (variable.data.value[refs.mode.value]) {
                render.main();
            } else {
                refs.loading.value = true;
                api.frontend("/api/gene/expression", {
                    data: {
                        id: route.params.id,
                        sort: refs.mode.value,
                        type: define.type
                    },
                    finally: () => (refs.loading.value = false),
                    success: (response) => {
                        if (response?.data?.length) {
                            //* 数据初始化
                            refs.tags.value = response?.tags || [];
                            variable.data.value[refs.mode.value] = response.data;
                            //* 渲染函数
                            render.main();
                        }
                    },
                });
            }
        },
    };

    //* 渲染函数
    const render = {
        //* 主函数
        main: () => {
            if (nodes.body.value && variable.data.value?.[refs.mode.value]?.length) {
                //* 舞台初始化
                if (variable.stage.value) {
                    variable.stage.value.removeChildren();
                    variable.stage.value.width(nodes.body.value.clientWidth);
                } else {
                    variable.stage.value = new Konva.Stage({ container: nodes.body.value, width: nodes.body.value.clientWidth, height: 324 });
                }

                //* 数据可视化数据处理
                const option = disposeVisualData(variable.data.value[refs.mode.value], {
                    size: nodes.body.value.clientWidth,
                    spacing: 8,
                    offset: variable.offset,
                });

                //* 场景初始化
                const canvas = new Konva.Layer();

                //* 内容加入场景
                canvas.add(render.other(option));
                canvas.add(render.axis(option));
                canvas.add(render.bar(variable.data.value[refs.mode.value], option));

                //* 场景加入舞台
                variable.stage.value.add(canvas);
            }
        },

        //* 标题与分割线
        other: (option: VisualData) => {
            //* 数据初始化
            const downLength = option.divisionY.down?.scales.length || 0;
            const upLength = option.divisionY.up.scales.length;
            const offset = upLength * 96 + 32;
            const height = (upLength + downLength) * 96 + 32;
            const width = (nodes.body.value?.clientWidth || 0) - option.spacing * 2 - 48;

            //* 容器
            const Other = new Konva.Group({ x: option.spacing });
            //* 标题
            const Name = new Konva.Text({
                x: 8,
                y: height / 2,
                fill: "#384053",
                text: define.type == "tissue" ? "RPKM" : "nTPM",
                rotation: -90,
                fontSize: 16,
                fontStyle: "bold",
                fontFamily: "sans-serif",
            });

            //* 内容加入容器
            Other.add(Name.offsetX(Name.getWidth() / 2));
            Other.add(new Konva.Rect({ x: option.offset, y: offset - 0.5, width: width, height: 1, fill: "#c7c7c7" }));
            Other.add(
                new Konva.Text({
                    x: option.offset,
                    y: offset - 20,
                    text: "0",
                    fill: "#6D747F",
                    fontSize: 12,
                    fontFamily: "Noto Sans CJK Regular",
                })
            );

            //* 检测是否存在负值坐标轴
            if (option.divisionY.down) {
                Other.add(new Konva.Rect({ x: option.offset, y: offset + downLength * 96 - 0.5, width: width, height: 1, fill: "#c7c7c7" }));
            }

            //* 设置图形高度
            variable.stage.value?.height(height);

            return Other;
        },

        //* 坐标系
        axis: (option: VisualData) => {
            //* 坐标系容器
            const Axis = new Konva.Group({ x: option.spacing + option.offset, y: 32 });
            //* 循环绘制坐标系
            option.divisionY.up.scales.forEach((division, index) => {
                //* 位置初始化
                const offset = 96 * (option.divisionY.up.number - index - 1);
                //* 元素加入容器
                Axis.add(new Konva.Rect({ x: 0, y: offset - 0.5, width: option.rect, height: 1, fill: "#c7c7c7" }));
                Axis.add(
                    new Konva.Text({
                        x: 0,
                        y: offset - 20,
                        text: Formats.validFigure(division, option.divisionY.decimals, true),
                        fill: "#6D747F",
                        fontSize: 12,
                        fontFamily: "Noto Sans CJK Regular",
                    })
                );
            });

            //* 检测是否存在负值坐标轴
            if (option.divisionY.down) {
                //* 数据初始化
                const number = option.divisionY.down?.number || 0;
                //* 循环绘制坐标系
                option.divisionY.down.scales.forEach((division, index) => {
                    //* 数据初始化
                    const offset = 96 * (option.divisionY.up.number + index + 1);
                    const Text = new Konva.Text({
                        x: 0,
                        y: offset - 20,
                        text: `-${Formats.validFigure(division, option.divisionY.decimals, true)}`,
                        fill: "#6D747F",
                        fontSize: 12,
                        fontFamily: "Noto Sans CJK Regular",
                    });

                    //* 元素加入容器
                    Axis.add(Text);
                    if (index != number - 1) {
                        Axis.add(new Konva.Rect({ x: 0, y: offset - 0.5, width: option.rect, height: 1, fill: "#c7c7c7" }));
                    }
                });
            }

            return Axis;
        },

        //* 柱子
        bar: (data: ModuleExpression[], option: VisualData) => {
            //* 容器
            const Bars = new Konva.Group({ x: option.spacing + option.offset + option.rect, y: 32 });

            //* 数据
            const height = variable.stage.value?.height() || 0;
            const config = { size: height, origin: height, font_size: 13, offest: 13 * Math.sin(Math.PI / 6) };

            //* 数据遍历
            data.forEach((value, index) => {
                //* 数据初始化
                const size = value.value * option.divisionY.ratio;
                const upHeight = option.divisionY.up.number * 96;
                const offsetY = upHeight - size;

                //* 容器初始化
                const Bar = new Konva.Group({ x: option.divisionX * (index + 1) + option.size * index });
                //* 遮罩
                const Rect = new Konva.Rect({ x: 0, y: offsetY, width: option.size, height: size, fill: "#fff", opacity: 0 });
                //* 名称
                const NameText = new Konva.Text({
                    x: option.size - 7,
                    y: config.origin - 24,
                    text: value.name,
                    fill: "#6D747F",
                    rotation: -60,
                    fontSize: config.font_size,
                    fontFamily: "Noto Sans CJK Regular",
                });

                //* 比对图形高度
                config.size = Math.max(config.size, config.origin + config.offest + Math.sin(Math.PI / 3) * NameText.getTextWidth());

                //* 元素加入容器
                Bar.add(new Konva.Rect({ x: 0, y: offsetY, width: option.size, height: size, fill: value.color }));
                Bar.add(NameText.offsetX(NameText.getTextWidth()));
                Bar.add(Rect);
                //* 元素挂载事件
                Bar.on("mouseleave", () => {
                    nodes.animation.value?.reverse();
                    nodes.tooltip.value?.hidden();
                });

                Bar.on("mousemove", (e) => {
                    nodes.tooltip.value?.show("start", { pageX: e.evt.pageX, pageY: e.evt.pageY, initialization: true });
                });

                //* 移动事件重复赋值animation导致 Tween.reverse失效
                Bar.on("mouseover", () => {
                    //* 数据缓存
                    refs.info.value = value;
                    //* 补间动画
                    nodes.animation.value?.reverse();
                    nodes.animation.value = new Konva.Tween({ node: Rect, opacity: 0.3, duration: 0.3 });
                    nextTick(() => {
                        nodes.animation.value?.play();
                    });
                });

                //* 元素加入分组
                Bars.add(Bar);
            });

            //* 设置图形高度
            variable.stage.value?.height(config.size + 8);

            return Bars;
        },
    };

    //* 事件列表
    const events = {
        view: {
            mousemove: () => nodes.animation.value?.play(),
            mouseleave: () => nodes.animation.value?.reverse(),
        },
    };

    //* 计算属性
    const computeds = {
        option: computed(() => {
            if (define.type == "cell") {
                return options.cell;
            } else {
                return options.tissue;
            }
        }),
    };

    return { refs, nodes, events, methods, variable, computeds };
};
